import cn from "classnames"
import { useState } from "react"

import { Button, Checkbox, Flex, Select, Tag, Text } from "@/atoms"
import { getRequiredAccess, REQUIRED_ACCESS_TYPES } from "@/domain/ai-inventory"
import { useTranslation } from "@/hooks/useTranslation"
import { DownOutlined, RightOutlined } from "@/icons"
import { getOptionById } from "@/pages/GovernancePage/applications/policies/getDemoData"
import { toOptionLabel } from "@/pages/GovernancePage/applications/policies/optionValues"
import { CardTemplate } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/CardTemplate"

import stylesRuleCard from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleCard/RuleCard.module.scss"
import stylesRuleTitle from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleTitle/RuleTitle.module.scss"
import styles from "./HardenAgent.module.scss"

import type { RequiredAccessType } from "@/domain/ai-inventory"
import type { SuggestionRowProps } from "./HardenAgent.types"

const TOGGLE_KEYS = [" ", "Enter"]
const ACCESS_LABEL_KEYS: Record<RequiredAccessType, string> = {
  network: "networkAccess",
  "file-system": "fileSystemAccess",
  execution: "executionAccess"
}

const stop = (event: React.SyntheticEvent) => event.stopPropagation()

export const SuggestionRow = (props: SuggestionRowProps) => {
  const { suggestion, checked, editing, tools, onToggle, onChangeValue } = props
  const { condition, side } = suggestion
  const [showActions, setShowActions] = useState(false)
  const { t } = useTranslation("translation", {
    keyPrefix: "governance.ai.harden.modal"
  })
  const { t: tRules } = useTranslation("translation", {
    keyPrefix: "governance.ai.form.rules"
  })
  const { t: tAccess } = useTranslation("translation", {
    keyPrefix: "insights"
  })
  const option = getOptionById(condition.id)
  const isAllow = side === "allow"
  const selectOptions = [
    ...(option?.options ?? []),
    ...condition.value
      .filter((value) => !option?.options.some((item) => item.value === value))
      .map((value) => ({ label: toOptionLabel(condition.id, value), value }))
  ]
  const allowedActions =
    suggestion.id === "toolBehavior" && tools?.length ? tools : undefined

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      className={styles.suggestion}
      onClick={editing ? undefined : onToggle}
      onKeyDown={(event) => {
        if (editing || !TOGGLE_KEYS.includes(event.key)) return

        event.preventDefault()
        onToggle()
      }}
    >
      <CardTemplate type={side} variant="white">
        <Flex gap={10} align="flex-start" flex={1} className={styles.column}>
          <Checkbox
            checked={checked}
            tabIndex={-1}
            onClick={stop}
            onChange={onToggle}
          />
          <Flex vertical gap={16} flex={1} className={styles.column}>
            <Flex align="center" gap={10} wrap>
              <Tag
                className={cn(stylesRuleTitle.tag, styles.suggestionTag, {
                  [stylesRuleTitle.tagAllow]: isAllow,
                  [stylesRuleTitle.tagNotAllow]: !isAllow
                })}
              >
                {isAllow ? "Allow" : "Don't allow"}
              </Tag>
              <Text className={styles.explanation}>
                {t(`explain.${suggestion.id}`)}
              </Text>
            </Flex>
            <Flex vertical gap={6}>
              <Flex
                align="center"
                gap={6}
                wrap
                className={stylesRuleCard.wrapper}
              >
                <Text className={stylesRuleCard.nowrap}>
                  {tRules(condition.id)}
                </Text>
                {option?.scope && (
                  <Text className={stylesRuleCard.nowrap} type="secondary">
                    {tRules(`scopes.${option.scope}`)}
                  </Text>
                )}
                {editing && (
                  <span className={styles.valueSelect} onClick={stop}>
                    <Select
                      mode="tags"
                      className={styles.valueSelect}
                      value={condition.value}
                      options={selectOptions}
                      onChange={onChangeValue}
                    />
                  </span>
                )}
                {!editing &&
                  condition.value.map((value) => (
                    <Text key={value} className={stylesRuleCard.tag}>
                      {toOptionLabel(condition.id, value)}
                    </Text>
                  ))}
              </Flex>
              {allowedActions && (
                <Button
                  type="link"
                  size="small"
                  className={styles.allowedActions}
                  icon={showActions ? <DownOutlined /> : <RightOutlined />}
                  onClick={(event) => {
                    event.stopPropagation()
                    setShowActions((prev) => !prev)
                  }}
                >
                  {t("allowedActions")}
                </Button>
              )}
              {allowedActions && showActions && (
                <Flex vertical gap={6} className={styles.actionsList}>
                  {allowedActions.map((tool) => {
                    const access = getRequiredAccess(tool)
                    const granted = REQUIRED_ACCESS_TYPES.filter(
                      (type) => access[type]
                    )

                    return (
                      <Flex key={tool.name} align="center" gap={8} wrap>
                        <Text className={stylesRuleCard.tag}>{tool.name}</Text>
                        {granted.length === 0 && (
                          <Text type="secondary">{t("noAccess")}</Text>
                        )}
                        {granted.map((type) => (
                          <Text key={type} type="secondary">
                            {tAccess(ACCESS_LABEL_KEYS[type])}
                          </Text>
                        ))}
                      </Flex>
                    )
                  })}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </CardTemplate>
    </div>
  )
}
